/**
 * CalendarSettingsDialog - Dialog de configuració del Calendar V2
 * Permet configurar opcions de visualització com mostrar/ocultar reserves cancelades
 * i l'ordre de les habitacions, i si les reserves es mouen i s'allarguen amb el ratolí (runbook 7, F3).
 */

import { useState, useSyncExternalStore } from 'react';
import { Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import type { DragMode } from './grid/useGridPointer';
import {
  CARD_RADIUS_PX,
  RIGHT_RADIUS_PX,
  type CardRadiusSetting,
  type RightRadiusSetting,
} from './calendar-radius';
import { ROW_HEIGHT_PX, type RowHeightSetting } from './grid/row-layout';

const STORAGE_KEY = 'calendarV2_settings';

export type RoomSortMode = 'sort_order' | 'nom_original' | 'display_name';
export type BarWidth = 4 | 6 | 8 | 10 | 12;
/** `compact` = columna de 140 px (`calendar-theme.tsx`). */
export type CalendarDensity = 'normal' | 'compact';
/** Arrodoniment de les cantonades d'entrada i sortida de la card (px a `calendar-theme.tsx`). */
export type CardRadius = CardRadiusSetting;
/** Cantonades de la dreta (`plans/calendar-card-signals.md` D3): 0 / 4 / 14 px. */
export type RightRadius = RightRadiusSetting;
/** Alçada de les files: Normal 75 px, Compacta 60 px, Mínima 46 px (`grid/row-layout.ts`). */
export type RowHeight = RowHeightSetting;

export interface CalendarSettings {
  showCancelled: boolean;
  roomSortMode: RoomSortMode;
  /** `mouse` = moure de fila i allargar amb el ratolí; `off` = ni nansa ni arrossegament. */
  dragMode: DragMode;
  /** Gruix de la barra del canal, en px (`plans/calendar-visual-refresh.md` D7). */
  barWidth: BarWidth;
  /** Amplada de la columna de dia (D7). */
  density: CalendarDensity;
  /**
   * Cantonades del dia d'entrada i del de sortida (en Pere, 29-SET: que es distingeixin dels dies
   * del mig i del tall del calendari, on la card queda recta).
   */
  cardRadius: CardRadius;
  /**
   * Cantonades de la dreta (D3, 30-SET): rectes per defecte, perquè hi van els tres llums. Amb
   * `clipEnd` (continua fora del que es veu), sempre rectes.
   */
  rightRadius: RightRadius;
  /** Alçada de les files (F7, en Pere 29-SET: Compacta per defecte). */
  rowHeight: RowHeight;
  /** La barra de dalt del calendari plegada en una tira fina (F7). Per dispositiu. */
  headerCollapsed: boolean;
}

export const BAR_WIDTH_OPTIONS: readonly BarWidth[] = [4, 6, 8, 10, 12];

const ROW_HEIGHT_OPTIONS: ReadonlyArray<{ value: RowHeight; label: string }> = [
  { value: 'normal', label: 'Normal' },
  { value: 'compact', label: 'Compacta' },
  { value: 'minimal', label: 'Mínima' },
];

const CARD_RADIUS_OPTIONS: ReadonlyArray<{ value: CardRadius; label: string }> = [
  { value: 'small', label: 'Poc' },
  { value: 'medium', label: 'Mitjà' },
  { value: 'large', label: 'Molt' },
];

const RIGHT_RADIUS_OPTIONS: ReadonlyArray<{ value: RightRadius; label: string }> = [
  { value: 'square', label: 'Rectes' },
  { value: 'small', label: 'Poc' },
  { value: 'round', label: 'Rodones' },
];

/**
 * En Pere (29-SET): barra més gruixuda (8 px), cantonades ben rodones i files compactes (60 px)
 * per defecte. 30-SET (D3): la dreta, recta.
 */
export const DEFAULT_SETTINGS: CalendarSettings = {
  showCancelled: false,
  roomSortMode: 'sort_order',
  dragMode: 'mouse',
  barWidth: 8,
  density: 'normal',
  cardRadius: 'large',
  rightRadius: 'square',
  rowHeight: 'compact',
  headerCollapsed: false,
};

function toBarWidth(value: unknown): BarWidth {
  const px = Number(value);
  return (BAR_WIDTH_OPTIONS as readonly number[]).includes(px)
    ? (px as BarWidth)
    : DEFAULT_SETTINGS.barWidth;
}

function toCardRadius(value: unknown): CardRadius {
  return value === 'small' || value === 'medium' || value === 'large'
    ? value
    : DEFAULT_SETTINGS.cardRadius;
}

function toRightRadius(value: unknown): RightRadius {
  return value === 'square' || value === 'small' || value === 'round'
    ? value
    : DEFAULT_SETTINGS.rightRadius;
}

function toRowHeight(value: unknown): RowHeight {
  return value === 'normal' || value === 'compact' || value === 'minimal'
    ? value
    : DEFAULT_SETTINGS.rowHeight;
}

/**
 * `calendarV2_settings` del localStorage → configuració completa. Un valor vell sense `dragMode`
 * (o amb un valor que no toca) queda en `mouse`; sense `barWidth`, `density`, `cardRadius`,
 * `rightRadius`, `rowHeight` o `headerCollapsed` vàlids, els per defecte; un JSON trencat, la configuració per
 * defecte.
 */
export function parseCalendarSettings(raw: string | null): CalendarSettings {
  if (!raw) return DEFAULT_SETTINGS;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return DEFAULT_SETTINGS;
    const merged = { ...DEFAULT_SETTINGS, ...(parsed as Partial<CalendarSettings>) };
    return {
      ...merged,
      dragMode: merged.dragMode === 'off' ? 'off' : 'mouse',
      barWidth: toBarWidth(merged.barWidth),
      density: merged.density === 'compact' ? 'compact' : 'normal',
      cardRadius: toCardRadius(merged.cardRadius),
      rightRadius: toRightRadius(merged.rightRadius),
      rowHeight: toRowHeight(merged.rowHeight),
      headerCollapsed: merged.headerCollapsed === true,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

interface CalendarSettingsDialogProps {
  settings: CalendarSettings;
  onSettingsChange: (settings: CalendarSettings) => void;
}

export function CalendarSettingsDialog({
  settings,
  onSettingsChange,
}: CalendarSettingsDialogProps) {
  const [open, setOpen] = useState(false);

  const handleShowCancelledChange = (checked: boolean) => {
    onSettingsChange({ ...settings, showCancelled: checked });
  };

  const handleRoomSortModeChange = (value: string) => {
    onSettingsChange({ ...settings, roomSortMode: value as RoomSortMode });
  };

  const handleDragModeChange = (value: string) => {
    onSettingsChange({ ...settings, dragMode: value === 'off' ? 'off' : 'mouse' });
  };

  const handleBarWidthChange = (value: string) => {
    onSettingsChange({ ...settings, barWidth: toBarWidth(value) });
  };

  const handleCardRadiusChange = (value: string) => {
    onSettingsChange({ ...settings, cardRadius: toCardRadius(value) });
  };

  const handleRightRadiusChange = (value: string) => {
    onSettingsChange({ ...settings, rightRadius: toRightRadius(value) });
  };

  const handleRowHeightChange = (value: string) => {
    onSettingsChange({ ...settings, rowHeight: toRowHeight(value) });
  };

  const handleDensityChange = (value: string) => {
    onSettingsChange({ ...settings, density: value === 'compact' ? 'compact' : 'normal' });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
          aria-label="Configuració del calendari"
        >
          <Settings className="w-5 h-5 text-gray-600" />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configuració del Calendari</DialogTitle>
          <DialogDescription>
            Personalitza la visualització del calendari segons les teves preferències.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-5">
          {/* Checkbox: Veure cancelades */}
          <div className="flex items-center space-x-3">
            <Checkbox
              id="show-cancelled"
              checked={settings.showCancelled}
              onCheckedChange={handleShowCancelledChange}
            />
            <Label
              htmlFor="show-cancelled"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Veure reserves cancel·lades
            </Label>
          </div>
          <p className="text-xs text-muted-foreground ml-7 -mt-3">
            Mostra o oculta les reserves cancel·lades al calendari
          </p>

          <Separator />

          {/* RadioGroup: Ordre habitacions */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-gray-800">Ordre de les habitacions</Label>
            <RadioGroup
              value={settings.roomSortMode}
              onValueChange={handleRoomSortModeChange}
              className="space-y-2"
            >
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="sort_order" id="sort-physical" />
                <Label htmlFor="sort-physical" className="cursor-pointer font-normal">
                  <span className="text-sm font-medium">Ordre físic</span>
                  <span className="block text-xs text-muted-foreground">
                    Per planta (H101, H102… H301, H302…)
                  </span>
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="display_name" id="sort-display" />
                <Label htmlFor="sort-display" className="cursor-pointer font-normal">
                  <span className="text-sm font-medium">Nom visualització</span>
                  <span className="block text-xs text-muted-foreground">
                    Alfabètic per nom configurat (H101, H102…)
                  </span>
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="nom_original" id="sort-original" />
                <Label htmlFor="sort-original" className="cursor-pointer font-normal">
                  <span className="text-sm font-medium">Nom original Beds24</span>
                  <span className="block text-xs text-muted-foreground">
                    Ordre original de Beds24 (1, 2, 3…)
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <Separator />

          {/* RadioGroup: moure i allargar amb el ratolí */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-gray-800">
              Moure i allargar reserves amb el ratolí
            </Label>
            <RadioGroup
              value={settings.dragMode}
              onValueChange={handleDragModeChange}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="mouse" id="drag-mode-mouse" />
                <Label htmlFor="drag-mode-mouse" className="cursor-pointer font-normal">
                  Sí
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="off" id="drag-mode-off" />
                <Label htmlFor="drag-mode-off" className="cursor-pointer font-normal">
                  No
                </Label>
              </div>
            </RadioGroup>
            <p className="text-xs text-muted-foreground">
              Sempre amb confirmació. Amb el dit no s'arrossega mai: al mòbil, el diàleg de la fitxa.
            </p>
          </div>

          <Separator />

          {/* RadioGroup: gruix de la barra del canal (D7) */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-gray-800">
              Gruix de la barra del canal
            </Label>
            <RadioGroup
              value={String(settings.barWidth)}
              onValueChange={handleBarWidthChange}
              className="flex flex-wrap gap-x-5 gap-y-2"
            >
              {BAR_WIDTH_OPTIONS.map((px) => (
                <div key={px} className="flex items-center space-x-2">
                  <RadioGroupItem value={String(px)} id={`bar-width-${px}`} />
                  <Label
                    htmlFor={`bar-width-${px}`}
                    className="flex items-center gap-1.5 cursor-pointer font-normal"
                  >
                    <span
                      aria-hidden
                      className="inline-block h-4 rounded-sm bg-slate-500"
                      style={{ width: px }}
                    />
                    {px} px
                  </Label>
                </div>
              ))}
            </RadioGroup>
            <p className="text-xs text-muted-foreground">
              Els colors de cada canal es trien a Configuració → Canals.
            </p>
          </div>

          <Separator />

          {/* RadioGroup: densitat (amplada de la columna de dia, D7) */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-gray-800">Densitat</Label>
            <RadioGroup
              value={settings.density}
              onValueChange={handleDensityChange}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="normal" id="density-normal" />
                <Label htmlFor="density-normal" className="cursor-pointer font-normal">
                  Normal
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="compact" id="density-compact" />
                <Label htmlFor="density-compact" className="cursor-pointer font-normal">
                  Compacta
                </Label>
              </div>
            </RadioGroup>
            <p className="text-xs text-muted-foreground">
              Compacta: columnes més estretes, hi caben més dies a la pantalla.
            </p>
          </div>

          <Separator />

          {/* RadioGroup: alçada de les files (F7) */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-gray-800">Alçada de les files</Label>
            <RadioGroup
              value={settings.rowHeight}
              onValueChange={handleRowHeightChange}
              className="flex flex-wrap gap-x-6 gap-y-2"
            >
              {ROW_HEIGHT_OPTIONS.map(({ value, label }) => (
                <div key={value} className="flex items-center space-x-2">
                  <RadioGroupItem value={value} id={`row-height-${value}`} />
                  <Label htmlFor={`row-height-${value}`} className="cursor-pointer font-normal">
                    {label}{' '}
                    <span className="text-xs text-muted-foreground">({ROW_HEIGHT_PX[value]} px)</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
            <p className="text-xs text-muted-foreground">
              Més baixes, hi caben més habitacions. Quan la reserva no hi cap sencera, les persones,
              les nits i l'idioma surten en passar el ratolí pel nom.
            </p>
          </div>

          <Separator />

          {/* RadioGroup: arrodoniment de les cantonades de l'esquerra (entrada) */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-gray-800">
              Cantonades de l'esquerra
            </Label>
            <RadioGroup
              value={settings.cardRadius}
              onValueChange={handleCardRadiusChange}
              className="flex gap-6"
            >
              {CARD_RADIUS_OPTIONS.map(({ value, label }) => (
                <div key={value} className="flex items-center space-x-2">
                  <RadioGroupItem value={value} id={`card-radius-${value}`} />
                  <Label
                    htmlFor={`card-radius-${value}`}
                    className="flex items-center gap-1.5 cursor-pointer font-normal"
                  >
                    <span
                      aria-hidden
                      className="inline-block h-4 w-6 border border-slate-400 bg-white"
                      style={{ borderRadius: CARD_RADIUS_PX[value] / 2 }}
                    />
                    {label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            <p className="text-xs text-muted-foreground">
              Arrodonides on la reserva entra; rectes on ve de fora del que es veu.
            </p>
          </div>

          <Separator />

          {/* RadioGroup: cantonades de la dreta (sortida), on van els llums (D3) */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-gray-800">
              Cantonades de la dreta
            </Label>
            <RadioGroup
              value={settings.rightRadius}
              onValueChange={handleRightRadiusChange}
              className="flex gap-6"
            >
              {RIGHT_RADIUS_OPTIONS.map(({ value, label }) => (
                <div key={value} className="flex items-center space-x-2">
                  <RadioGroupItem value={value} id={`right-radius-${value}`} />
                  <Label
                    htmlFor={`right-radius-${value}`}
                    className="flex items-center gap-1.5 cursor-pointer font-normal"
                  >
                    <span
                      aria-hidden
                      className="inline-block h-4 w-6 border border-slate-400 bg-white"
                      style={{
                        borderTopRightRadius: RIGHT_RADIUS_PX[value] / 2,
                        borderBottomRightRadius: RIGHT_RADIUS_PX[value] / 2,
                      }}
                    />
                    {label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            <p className="text-xs text-muted-foreground">
              On la reserva surt i hi ha els llums. Rectes on continua fora del que es veu.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Un sol valor compartit per tots els que criden `useCalendarSettings` (la pàgina, la graella i el
// tema del calendari): un canvi a la roda dentada arriba a tots alhora.
let currentSettings: CalendarSettings | null = null;
const listeners = new Set<() => void>();

function readSettings(): CalendarSettings {
  if (currentSettings) return currentSettings;
  try {
    currentSettings = parseCalendarSettings(localStorage.getItem(STORAGE_KEY));
  } catch {
    currentSettings = DEFAULT_SETTINGS;
  }
  return currentSettings;
}

function writeSettings(
  next: CalendarSettings | ((prev: CalendarSettings) => CalendarSettings)
): void {
  currentSettings = typeof next === 'function' ? next(readSettings()) : next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentSettings));
  } catch {
    // Sense localStorage (navegació privada): la configuració dura fins que es tanca la pestanya.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Una altra pestanya ha desat: es torna a llegir, i així no s'hi trepitja el que ha canviat.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) return;
    currentSettings = null;
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** Només per als tests: oblida el valor compartit perquè es torni a llegir del localStorage. */
export function resetCalendarSettingsCache(): void {
  currentSettings = null;
}

/**
 * Configuració del calendari (localStorage `calendarV2_settings`), compartida entre components.
 */
export function useCalendarSettings() {
  const settings = useSyncExternalStore(subscribe, readSettings, readSettings);
  return { settings, setSettings: writeSettings };
}
